import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MapPin, ExternalLink, TreePine } from "lucide-react";
import HotspotPlan from "../components/site/HotspotPlan";
import AreaMapView from "../components/site/AreaMapView";
import Reveal from "../components/site/Reveal";
import CountUp from "../components/site/CountUp";
import { useReveal } from "../lib/useReveal";

const DEER_PARK_REGION = { x: 0.135, y: 0.17, w: 0.15, h: 0.17, label: "Deer Park Estates" };

const STATUS = {
  existing: { label: "Existing", color: "#3E7A5E" },
  under_construction: { label: "Under construction", color: "#4A7BA5" },
  approved: { label: "Approved", color: "#5B6B7A" },
  planned: { label: "Planned", color: "#C79A3B" },
  proposed: { label: "Proposed", color: "#B0632B" },
  conceptual: { label: "Conceptual", color: "#8A4B4B" },
};

const Location = () => {
  const [sec, setSec] = useState({});
  const [project, setProject] = useState({});
  useEffect(() => {
    api.get("/sections/location").then((r) => setSec(r.data || {})).catch(() => {});
    api.get("/project").then((r) => setProject(r.data || {})).catch(() => {});
  }, []);

  const amenities = sec.amenities || [];
  const categories = [...new Set(amenities.map((a) => a.category))];
  useReveal([amenities.length, sec.highlights?.length || 0]);

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <section className="border-b border-[#ffffff14] bg-[#0F1B15]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16">
          <p className="eyebrow text-[#C9B79C] mb-4 flex items-center gap-2"><MapPin size={14} /> About &amp; Location</p>
          <Reveal as="h1" className="font-display font-light text-4xl md:text-6xl text-[#F3EEE3]">{sec.title || "About & Location"}</Reveal>
          <p className="text-[#F3EEE3]/75 mt-5 max-w-3xl text-lg leading-relaxed">{sec.intro}</p>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-14">
        {/* highlights */}
        {sec.highlights?.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sec.highlights.map((h, i) => (
              <div key={i} style={{ transitionDelay: `${(i % 3) * 70}ms` }} className="reveal rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-6">
                <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-2 flex items-center gap-2"><TreePine size={12} /> {h.label}</p>
                <p className="font-display text-xl text-[#F3EEE3] leading-snug"><CountUp value={h.value} /></p>
              </div>
            ))}
          </div>
        )}

        {sec.body && <p className="text-[#F3EEE3]/75 max-w-3xl leading-relaxed mt-12">{sec.body}</p>}

        {/* City of Spruce Grove area map */}
        {project.area_map_image && (
          <div className="mt-12" data-testid="area-map">
            <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mb-2">Explore Spruce Grove</h2>
            <p className="text-[#F3EEE3]/60 text-sm mb-4 max-w-2xl">Deer Park sits in the northwest of Spruce Grove. Zoom and pan the City parks &amp; recreation map to see parks, schools, trails and amenities across town.</p>
            <AreaMapView mapSrc={project.area_map_image} infoSrc={project.area_info_image} />
            <a href="https://www.sprucegrove.org/recreation" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-3 hover:text-[#F3EEE3]">Map source: City of Spruce Grove <ExternalLink size={12} /></a>
          </div>
        )}

        {/* amenities by category */}
        <h2 className="font-display text-2xl md:text-3xl text-[#F3EEE3] mt-14 mb-2">Nearby amenities</h2>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-8 mt-4">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-2 text-[0.7rem] text-[#F3EEE3]/55">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} /> {v.label}
            </span>
          ))}
        </div>

        {categories.map((cat) => (
          <div key={cat} className="mb-8">
            <p className="eyebrow text-[0.55rem] text-[#C9B79C] mb-3">{cat}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {amenities.filter((a) => a.category === cat).map((a, i) => {
                const st = STATUS[a.status] || STATUS.existing;
                return (
                  <div key={i} data-testid={`amenity-${cat}-${i}`} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-display text-lg text-[#F3EEE3] leading-snug">{a.name}</p>
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-[0.6rem] rounded-full px-2.5 py-1" style={{ color: "#F3EEE3", background: st.color + "26", border: `1px solid ${st.color}` }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} /> {st.label}
                      </span>
                    </div>
                    <p className="text-[#F3EEE3]/60 text-sm mt-2">{a.note}</p>
                    {a.source && (
                      <a href={a.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#C9B79C] text-xs mt-3 hover:text-[#F3EEE3]">
                        Source <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {sec.disclaimer && <p className="text-[0.62rem] tracking-widest uppercase text-[#F3EEE3]/40 mt-6 leading-relaxed">{sec.disclaimer}</p>}
      </div>
    </div>
  );
};

export default Location;
